import { TaskPriority } from "./TaskPriorityTag";

export interface TaskContentClipboardTemplate {
  assignees: string[];
  content: string;
  start_time: number;
  due_time: number;
  priority: TaskPriority;
}

export interface TaskSubtaskClipboardTemplate {
  title: string;
  transferees: string[];
  reviewers: string[];
  content_items: TaskContentClipboardTemplate[];
}

type TaskTemplateKind = "subtask" | "content-item";

interface TaskTemplateEnvelope<T = unknown> {
  source: "heltec-project-management";
  version: 2;
  kind: TaskTemplateKind;
  data: T;
}

const STORAGE_KEY = "heltec-task-template-clipboard";
let memoryClipboard = "";

function parseEnvelope<T>(value: string, kind: TaskTemplateKind) {
  if (!value.trim()) return null;
  try {
    const parsed = JSON.parse(value) as Partial<TaskTemplateEnvelope<T>>;
    if (
      parsed.source !== "heltec-project-management" ||
      parsed.version !== 2 ||
      parsed.kind !== kind ||
      !parsed.data
    ) {
      return null;
    }
    return parsed.data as T;
  } catch {
    return null;
  }
}

export async function copyTaskTemplate<T>(kind: TaskTemplateKind, data: T) {
  const envelope: TaskTemplateEnvelope<T> = {
    source: "heltec-project-management",
    version: 2,
    kind,
    data,
  };
  const value = JSON.stringify(envelope);
  memoryClipboard = value;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Large pasted images can exceed browser storage; memory and system clipboard remain available.
  }
  try {
    await navigator.clipboard?.writeText(value);
  } catch {
    // Some browsers deny clipboard access outside HTTPS. The page-local copy still works.
  }
}

export async function readTaskTemplate<T>(kind: TaskTemplateKind) {
  try {
    const clipboardValue = await navigator.clipboard?.readText();
    const clipboardData = clipboardValue
      ? parseEnvelope<T>(clipboardValue, kind)
      : null;
    if (clipboardData) return clipboardData;
  } catch {
    // Fall through to the page-local clipboard.
  }

  const memoryData = parseEnvelope<T>(memoryClipboard, kind);
  if (memoryData) return memoryData;
  try {
    return parseEnvelope<T>(window.sessionStorage.getItem(STORAGE_KEY) || "", kind);
  } catch {
    return null;
  }
}
